import { __decorate, __metadata } from "tslib";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { setInteractionMode } from "vee-validate";
import _ from "lodash";
import BaseMixer from "@/router/views/BaseMixer";
import StrictDateMixin from "@/router/views/StrictDateMixin";
import UnsavedChangesMixin from "@/router/views/UnsavedChangesMixin";
import { toaster } from "@/utilities/toaster";
import { checkImageFile } from "@/utilities/imageFileCheck";
import { ClientModel } from "@/store/models/client/client";
import { AddressModel } from "@/store/models/shared/address";
import ClientModule from "@/store/modules/client.module";
import { DateFormatConstant } from "@/store/DateFormatConstants";
import { isIndia, RECHECK_MODE } from "@/types/validators";
import SectionLoader from "@/components/widgets/SectionLoader.vue";
setInteractionMode("passive");
/**
 * Edit Client — inline form rendered inside ClientProfileTabComponent
 * while the shared ClientOverviewMachine is in loadedClientContactsInfo.editPrimaryInfo.updateClient.
 */
let EditClientComponent = /** @class */ (() => {
    var _a;
    let EditClientComponent = class EditClientComponent extends Mixins(BaseMixer, StrictDateMixin, UnsavedChangesMixin) {
        constructor() {
            super(...arguments);
            // The form edits a copy, so Cancel leaves the saved client and the profile view untouched
            this.client = null;
            this.address = new AddressModel();
            this.previewImage = null;
            // The picked logo stays local (preview only) until Update uploads it
            this.pendingLogo = null;
            this.uploadedLogoFile = null;
            this.uploadedLogoId = "";
            this.uploadingLogo = false;
            this.recheckMode = RECHECK_MODE;
        }
        // Compares calendar days only; PSA Date may equal the Start Date.
        isPsaDateBeforeStartDate() {
            if (!this.client.startDate || !this.client.psaDate) {
                return false;
            }
            const startDate = new Date(this.client.startDate);
            const psaDate = new Date(this.client.psaDate);
            startDate.setHours(0, 0, 0, 0);
            psaDate.setHours(0, 0, 0, 0);
            return psaDate < startDate;
        }
        // Compares calendar days only; SS Date may equal the Start Date.
        isServiceStatementDateBeforeStartDate() {
            if (!this.client.startDate || !this.client.serviceStatementDate) {
                return false;
            }
            const startDate = new Date(this.client.startDate);
            const serviceStatementDate = new Date(this.client.serviceStatementDate);
            startDate.setHours(0, 0, 0, 0);
            serviceStatementDate.setHours(0, 0, 0, 0);
            return serviceStatementDate < startDate;
        }
        // SS Date / PSA Date pickers: no days before the Start Date (same day allowed)
        disableBeforeStartDate(date) {
            if (!this.client.startDate) {
                return false;
            }
            const startDate = new Date(this.client.startDate);
            startDate.setHours(0, 0, 0, 0);
            return date < startDate;
        }
        updateClientDetail() {
            if (this.uploadingLogo || this.saving) {
                return;
            }
            this.validate("newClientPrimaryInfoObserver").then((response) => {
                if (response) {
                    if (this.hasDateErrors) {
                        // The rejected date's message is already shown under its picker
                        return;
                    }
                    else if (this.isServiceStatementDateBeforeStartDate()) {
                        toaster.ShowError("SS Date should be the same as or later than the Start Date.");
                    }
                    else if (this.isPsaDateBeforeStartDate()) {
                        toaster.ShowError("PSA Date should be the same as or later than the Start Date.");
                    }
                    else {
                        this.uploadPendingLogo().then((uploaded) => {
                            if (uploaded) {
                                this.submitClient();
                            }
                        });
                    }
                }
            });
        }
        // Uploads the picked logo (if any) and keeps its id; false when the upload failed and the client must not be saved.
        // A logo already uploaded by an earlier, failed update isn't uploaded again.
        uploadPendingLogo() {
            if (!this.pendingLogo || (this.pendingLogo === this.uploadedLogoFile && this.uploadedLogoId)) {
                return Promise.resolve(true);
            }
            const logo = this.pendingLogo;
            const formData = new FormData();
            formData.append("file", logo);
            this.uploadingLogo = true;
            return ClientModule.UploadClientPicture({ file: formData, type: "logos" })
                .then((res) => {
                if (res) {
                    this.uploadedLogoFile = logo;
                    this.uploadedLogoId = res.mediaFileId;
                    return true;
                }
                toaster.ShowError("Client logo upload failed.");
                return false;
            }, () => false)
                .finally(() => {
                this.uploadingLogo = false;
            });
        }
        submitClient() {
            this.client.name = (this.client.name || "").trim();
            if (this.pendingLogo) {
                this.client.clientLogoId = this.uploadedLogoId;
            }
            let isEmpty = false;
            Object.entries(this.address).forEach(([key, value]) => {
                if (value != null) {
                    isEmpty = true;
                }
            });
            if (isEmpty) {
                this.client.addresses = [];
                this.client.addresses.push(this.address);
            }
            if (this.dateFormat === DateFormatConstant.DateFormat.DateFormatMDY()) {
                this.client.startDateAsString = this.client.startDate ? this.dateFormattedString(this.client.startDate) : "";
                this.client.serviceStatementDateAsString = this.client.serviceStatementDate ? this.dateFormattedString(this.client.serviceStatementDate) : "";
                this.client.sowDateAsString = this.client.sowDate ? this.dateFormattedString(this.client.sowDate) : "";
                this.client.psaDateAsString = this.client.psaDate ? this.dateFormattedString(this.client.psaDate) : "";
            }
            else {
                this.client.startDateAsString = this.client.startDate ? this.dateFormatted(new Date(this.client.startDate)) : "";
                this.client.serviceStatementDateAsString = this.client.serviceStatementDate ? this.dateFormatted(new Date(this.client.serviceStatementDate)) : "";
                this.client.sowDateAsString = this.client.sowDate ? this.dateFormatted(new Date(this.client.sowDate)) : "";
                this.client.psaDateAsString = this.client.psaDate ? this.dateFormatted(new Date(this.client.psaDate)) : "";
            }
            this.machineService.send({
                type: "UPDATING_CLIENT",
                payLoad: this.client,
            });
        }
        cancelEditClient() {
            this.confirmLeave().then((leave) => {
                if (leave) {
                    this.machineService.send({ type: "CLOSE_MODAL" });
                }
            });
        }
        // Checks the file's content and keeps it for Update; nothing is uploaded yet
        fileSelected3(e) {
            const input = e.target;
            const newFiles = input.files || e.dataTransfer.files;
            if (!newFiles.length)
                return;
            const file = newFiles[0];
            checkImageFile(file).then((error) => {
                if (error) {
                    // Clear the input so picking the same file again re-runs the check
                    input.value = "";
                    toaster.ShowError(error);
                    return;
                }
                this.pendingLogo = file;
                const reader = new FileReader();
                reader.onload = () => {
                    this.previewImage = reader.result;
                };
                reader.readAsDataURL(file);
            });
        }
        // The values that count as unsaved edits (see UnsavedChangesMixin)
        unsavedState() {
            return {
                client: this.client,
                logo: this.pendingLogo ? `${this.pendingLogo.name}:${this.pendingLogo.size}` : null,
            };
        }
        // Computed
        get countryLists() {
            return ClientModule.countryLists;
        }
        get selectedCountryName() {
            const countryId = this.client.addresses && this.client.addresses[0] ? this.client.addresses[0].countryId : null;
            const country = this.countryLists.find((x) => x.id.toString() === String(countryId));
            return country ? country.value : "";
        }
        get zipLabel() {
            return isIndia(this.selectedCountryName) ? "PIN Code" : "ZIP Code";
        }
        get stateLists() {
            if (this.client.addresses[0].countryId != null) {
                return ClientModule.stateLists.filter((x) => x.countryId == this.client.addresses[0].countryId);
            }
            return [];
        }
        get eligibleCSMs() {
            return ClientModule.eligibleCSMs;
        }
        get eligibleTSMs() {
            return ClientModule.eligibleTSMs;
        }
        //Lifecycle
        created() {
            const copy = _.cloneDeep(this.sourceClient);
            // The date-pickers only render Date values; the saved client keeps its original values
            copy.startDate = copy.startDate ? new Date(copy.startDate) : null;
            copy.serviceStatementDate = copy.serviceStatementDate ? new Date(copy.serviceStatementDate) : null;
            copy.sowDate = copy.sowDate ? new Date(copy.sowDate) : null;
            copy.psaDate = copy.psaDate ? new Date(copy.psaDate) : null;
            this.client = copy;
        }
    };
    __decorate([
        Prop(),
        __metadata("design:type", typeof (_a = typeof ClientModel !== "undefined" && ClientModel) === "function" ? _a : Object)
    ], EditClientComponent.prototype, "sourceClient", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], EditClientComponent.prototype, "machineService", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], EditClientComponent.prototype, "saving", void 0);
    EditClientComponent = __decorate([
        Component({
            name: "EditClientComponent",
            components: {
                SectionLoader,
            },
        })
    ], EditClientComponent);
    return EditClientComponent;
})();
export default EditClientComponent;
